<template>
  <div class="inner">
    <div
      class="mx-auto my-3 alert w-1/2 alert-error"
      v-if="!loadingData && error">
      <AlertCircle />
      <span>{{ error }}</span>
    </div>

    <div class="flex flex-col gap-4">
      <div>
        <h1 data-test="title">Commission Yield Analysis</h1>

        <FilterBar class="mt-1 items-center">
          <GenericMultiFilter
            :options="coverageTypeOptions"
            v-model="selectedCoverageTypes"
            title="Coverage Type"
            chip-title-field="label"
            data-test="coverage-type-filter"
            @update:model-value="refreshData" />
          <GenericMultiFilter
            :options="carrierGroupOptions"
            v-model="selectedCarrierGroups"
            title="Carrier Group"
            chip-title-field="label"
            data-test="carrier-group-filter"
            @update:model-value="refreshData" />
          <GenericRangeFilter
            v-model="commissionRange"
            title="Commission %"
            min-title="Min %"
            max-title="Max %"
            :suggested-range="[0, 100]"
            :value-formatter-fn="(val) => (val != null ? filters.percentage(val / 100) : '')"
            data-test="commission-range-filter"
            data-test-prefix="commission-range"
            @update:model-value="refreshData" />
          <DateRangeSelector
            class="mr-0 ml-auto"
            :initial-start-date="startDate"
            :initial-end-date="endDate"
            :on-apply="setDate" />
        </FilterBar>
      </div>

      <div>
        <div class="flex gap-2">
          <h2>Commission Percentage</h2>
          <download-button @click="downloadTable" />
        </div>
      </div>
    </div>

    <div
      v-if="loadingData && !error"
      class="mt-8 flex flex-col gap-2">
      <Skeleton
        v-for="i in 12"
        :key="i"
        shape="rectangle"
        class="w-full"
        height="20px" />
    </div>

    <span
      class="block text-center"
      v-if="!loadingData && commissionData.length === 0"
      >No data available.</span
    >

    <div
      v-if="!loadingData && commissionData.length > 0"
      class="commission-data overflow-x-auto">
      <table
        id="commission-data-table"
        class="w-full border-collapse">
        <thead>
          <tr>
            <th
              class="text-center"
              colspan="1">
              Carrier
            </th>
            <th
              class="w-40 text-center"
              colspan="4">
              Totals
            </th>
            <th
              v-for="coverage in coverageTypes"
              :key="coverage"
              colspan="4"
              class="text-center">
              {{ coverage }}
            </th>
          </tr>
          <tr>
            <th
              v-for="col in headers"
              :key="col.key"
              class="px-2 py-1 text-left">
              {{ col.title }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(item, idx) in commissionData"
            :key="item.name + String(idx)">
            <td class="commission px-2 py-1">{{ item.name }}</td>
            <td class="px-2 py-1">{{ item.min }}</td>
            <td class="px-2 py-1">{{ item.max }}</td>
            <td class="px-2 py-1">{{ item.avg }}</td>
            <td class="commission px-2 py-1">{{ item.wAvg }}</td>
            <template
              v-for="coverage in coverageTypes"
              :key="coverage">
              <td class="px-2 py-1">{{ item[`min-val-${coverage}`] }}</td>
              <td class="px-2 py-1">{{ item[`max-val-${coverage}`] }}</td>
              <td class="px-2 py-1">{{ item[`avg-val-${coverage}`] }}</td>
              <td class="commission px-2 py-1">{{ item[`wavg-val-${coverage}`] }}</td>
            </template>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { DateTime } from 'luxon';
import { percentage } from '@/utilities/Number';
import FilterBar from '@/components/v2/FilterBar.vue';
import GenericMultiFilter from '@/components/v2/GenericMultiFilter.vue';
import GenericRangeFilter from '@/components/v2/GenericRangeFilter.vue';
import DateRangeSelector from '@/components/v2/DateRangeSelector.vue';
import { FailedToLoadPage } from '@/utilities/Errors';
import { carrierService, referenceService } from '@/services/api';
import { DownloadTableToCSV } from '@/utilities/Download';
import { filters } from '@/utilities/Filters';
import { AlertCircle } from '@lucide/vue';
import DownloadButton from '@/components/v2/DownloadButton.vue';
import Skeleton from '@/components/v2/Skeleton.vue';

export default {
  name: 'YieldAnalysis',
  components: {
    DownloadButton,
    AlertCircle,
    FilterBar,
    GenericMultiFilter,
    GenericRangeFilter,
    DateRangeSelector,
    Skeleton,
  },
  data: () => ({
    filters,
    list: [],
    commissions: [],
    error: undefined,
    loadingData: true,
    filterText: '',
    selectedCarrierGroups: [],
    selectedCoverageTypes: [],
    allCoverageTypes: [],
    allCarrierGroups: [],
    startDate: DateTime.local().minus({ days: 366 }).toJSDate(),
    endDate: DateTime.local().minus({ days: 1 }).toJSDate(),
    commissionRange: [],
    groupedResults: [],
  }),
  methods: {
    async refreshData() {
      if (this.created && this.loadingData) {
        return;
      }
      this.loadingData = true;

      [this.list, this.commissions] = await Promise.all([
        carrierService.list(),
        carrierService.findCommissions(DateTime.fromJSDate(this.startDate), DateTime.fromJSDate(this.endDate)),
      ]);

      if (this.commissions) {
        const [minPct, maxPct] = this.commissionRange;
        this.groupedResults = this.commissions.reduce((acc, curr) => {
          const commission = Number(curr.Commission);
          if (minPct != null && commission < minPct) return acc;
          if (maxPct != null && commission > maxPct) return acc;
          acc[curr.InsurerGroup.toLowerCase()] = acc[curr.InsurerGroup.toLowerCase()] || [];
          acc[curr.InsurerGroup.toLowerCase()].push(curr);
          return acc;
        }, {});
      }

      //filter out carriers with no commissions
      this.allCarrierGroups = this.allCarrierGroups.filter(
        (x) => this.groupedResults[x.value.toLowerCase()] && this.groupedResults[x.value.toLowerCase()].length !== 0,
      );

      //filter out coveragetypes with no commissions
      this.allCoverageTypes = this.allCoverageTypes.filter((coverage) =>
        this.commissions.some((x) => x.CoverageType.toLowerCase() === coverage.value.toLowerCase()),
      );

      this.loadingData = false;
    },
    async loadCoverageTypes() {
      this.allCoverageTypes = (await referenceService.coverageTypes())
        .filter((item) => item.label.toLowerCase() !== 'fees')
        .map((ct) => ({ text: ct.label, value: ct.label }));
    },
    async loadCarrierGroups() {
      this.allCarrierGroups = (await referenceService.carrierGroups()).map((ct) => ({
        text: ct.label,
        value: ct.label,
      }));
    },
    async refreshFilters() {
      this.loadingFilters = true;
      await Promise.all([this.loadCoverageTypes(), this.loadCarrierGroups()]);
      this.loadingFilters = false;
    },
    setDate(datePayload) {
      if (datePayload.startDate) {
        this.startDate = datePayload.startDate;
      }
      if (datePayload.endDate) {
        this.endDate = datePayload.endDate;
      }
      this.refreshData();
    },
    getAllForName(name) {
      return this.groupedResults[name.toLowerCase()] || [];
    },
    getAllForType(name, type) {
      return (this.groupedResults[name.toLowerCase()] || []).filter((x) => x.CoverageType.toLowerCase() === type.toLowerCase()) || [];
    },
    findMinForGroup(name) {
      const allCommissions = this.getAllForName(name).filter((x) => this.coverageTypes.find((y) => y === x.CoverageType));
      const result = allCommissions.reduce((acc, curr) => (curr.Commission < Number(acc.Commission) ? curr : acc), allCommissions[0]);
      return result ? percentage(Number(result.Commission), 1) : '';
    },
    findMaxForGroup(name) {
      const allCommissions = this.getAllForName(name).filter((x) => this.coverageTypes.find((y) => y === x.CoverageType));
      const result = allCommissions.reduce((acc, curr) => (curr.Commission > Number(acc.Commission) ? curr : acc), allCommissions[0]);
      return result ? percentage(Number(result.Commission), 1) : '';
    },
    findAvgForGroup(name) {
      let allCommissions = this.getAllForName(name).filter((x) => this.coverageTypes.find((y) => y === x.CoverageType));
      const result = allCommissions.reduce((acc, curr) => acc + Number(curr.Commission), 0) / allCommissions.length;
      return isFinite(result) ? percentage(Number(result), 1) : '';
    },
    findSumAvgForGroup(name) {
      const allCommissions = this.getAllForName(name).filter((x) => this.coverageTypes.find((y) => y === x.CoverageType));
      let sumCommsBilled = allCommissions.reduce((acc, curr) => acc + Number(curr.CommissionBilled), 0);
      let sumPolicyPremium = allCommissions.reduce((acc, curr) => acc + Number(curr.PolicyPremium), 0);

      const result = (sumCommsBilled / sumPolicyPremium) * 100;
      return isFinite(result) ? percentage(Number(result), 1) : '';
    },

    findMin(name, type) {
      const allCommissions = this.getAllForType(name, type);
      const result = allCommissions.reduce((acc, curr) => (curr.Commission < Number(acc.Commission) ? curr : acc), allCommissions[0]);
      return result ? percentage(Number(result.Commission), 1) : '';
    },
    findMax(name, type) {
      const allCommissions = this.getAllForType(name, type);
      const result = allCommissions.reduce((acc, curr) => (curr.Commission > Number(acc.Commission) ? curr : acc), allCommissions[0]);
      return result ? percentage(Number(result.Commission), 1) : '';
    },
    findAvg(name, type) {
      let allCommissions = this.getAllForType(name, type);
      const result = allCommissions.reduce((acc, curr) => acc + Number(curr.Commission), 0) / allCommissions.length;
      return isFinite(result) ? percentage(Number(result), 1) : '';
    },
    findSumAvg(name, type) {
      let allCommissions = this.getAllForType(name, type);
      let sumCommsBilled = allCommissions.reduce((acc, curr) => acc + Number(curr.CommissionBilled), 0);
      let sumPolicyPremium = allCommissions.reduce((acc, curr) => acc + Number(curr.PolicyPremium), 0);

      const result = (sumCommsBilled / sumPolicyPremium) * 100;
      return isFinite(result) ? percentage(Number(result), 1) : '';
    },
    downloadTable() {
      DownloadTableToCSV('#commission-data-table', 'Commission Percentage', true);
    },
  },
  computed: {
    coverageTypeOptions() {
      return this.allCoverageTypes.map((ct) => ({ label: ct.text, value: ct.value }));
    },
    carrierGroupOptions() {
      return this.allCarrierGroups.map((cg) => ({ label: cg.text, value: cg.value }));
    },
    headers() {
      return [
        {
          key: 'carrier',
          title: '',
          sortable: false,
          align: 'start',
          value: 'name',
          cellClass: 'commission',
          class: 'commission',
          width: '15rem',
        },
        {
          key: 'total-min',
          title: 'Min',
          sortable: false,
          align: 'end',
          value: 'min',
          cellClass: 'bold-total',
          width: '4rem',
        },
        {
          key: 'total-max',
          title: 'Max',
          sortable: false,
          align: 'end',
          value: 'max',
          cellClass: 'bold-total',
          width: '4rem',
        },
        {
          key: 'total-avg',
          title: 'Avg',
          sortable: false,
          align: 'end',
          value: 'avg',
          cellClass: 'bold-total',
          width: '4rem',
        },
        {
          key: 'total-w-avg',
          title: 'W-Avg',
          sortable: false,
          align: 'end',
          value: 'wAvg',
          cellClass: 'bold-total commission',
          class: 'commission',
          width: '4rem',
        },
        ...this.coverageTypes.flatMap((coverage) => [
          {
            key: `${coverage}-min`,
            title: 'Min',
            sortable: false,
            align: 'end',
            value: `min-val-${coverage}`,
            width: '4rem',
          },
          {
            key: `${coverage}-max`,
            title: 'Max',
            sortable: false,
            align: 'end',
            value: `max-val-${coverage}`,
            width: '4rem',
          },
          {
            key: `${coverage}-avg`,
            title: 'Avg',
            sortable: false,
            align: 'end',
            value: `avg-val-${coverage}`,
            width: '4rem',
          },
          {
            key: `${coverage}-w-avg`,
            title: 'W-Avg',
            sortable: false,
            align: 'end',
            class: 'commission',
            cellClass: 'commission',
            value: `wavg-val-${coverage}`,
          },
        ]),
      ];
    },
    commissionData() {
      return this.carrierGroups
        .map((carrier) => {
          const cData = {
            name: carrier,
            min: this.findMinForGroup(carrier) ?? '--',
            max: this.findMaxForGroup(carrier) ?? '--',
            avg: this.findAvgForGroup(carrier) ?? '--',
            wAvg: this.findSumAvgForGroup(carrier) ?? '--',
          };

          this.coverageTypes.forEach((coverage) => {
            cData[`min-val-${coverage}`] = this.findMin(carrier, coverage) ?? '--';
            cData[`max-val-${coverage}`] = this.findMax(carrier, coverage) ?? '--';
            cData[`avg-val-${coverage}`] = this.findAvg(carrier, coverage) ?? '--';
            cData[`wavg-val-${coverage}`] = this.findSumAvg(carrier, coverage) ?? '--';
          });

          return cData;
        })
        .filter((c) => {
          const vals = new Set([c.min, c.max, c.avg, c.wAvg]);
          return !(vals.size === 1 && vals.has('--')); // if all dashempty (--) then filter it out of the result
        });
    },
    coverageTypes() {
      if (this.selectedCoverageTypes.length === 0) {
        return this.allCoverageTypes.map((x) => x.value);
      }
      return this.allCoverageTypes
        .filter((x) => this.selectedCoverageTypes.find((y) => y.toLowerCase() === x.value.toLowerCase()))
        .map((x) => x.value);
    },
    carrierGroups() {
      if (this.selectedCarrierGroups.length > 0) {
        return this.allCarrierGroups
          .filter((x) => this.selectedCarrierGroups.find((y) => y.toLowerCase() === x.value.toLowerCase()))
          .map((x) => x.value);
      }
      return this.allCarrierGroups.map((x) => x.value);
    },
  },
  created() {
    try {
      void this.refreshFilters();
      void this.refreshData();
      return true;
    } catch (e) {
      console.error(e);
      this.error = FailedToLoadPage;
      return false;
    } finally {
      this.created = true;
    }
  },
};
</script>

<style scoped>
.commission-data,
.commission-data table {
  display: block;
  max-width: -moz-fit-content;
  max-width: fit-content;
  margin: 0 0;
  overflow-x: auto;
  white-space: nowrap;
}

.commission-data table > tbody > tr > td:nth-child(1),
.commission-data table > thead > tr > th:nth-child(1) {
  position: sticky !important;
  position: -webkit-sticky !important;
  left: 0;
  background: white;
}

.heading {
  display: grid;
  grid-template-columns: auto 1fr auto;
  grid-column-gap: 1rem;
  align-content: center;
  align-items: center;
}
</style>

<style>
.commission-data table > tbody > tr > td:nth-child(1),
.commission-data table > thead > tr > th:nth-child(1) {
  position: sticky !important;
  position: -webkit-sticky !important;
  left: 0;
  background: white;
}

.bold-total {
  font-weight: bold;
}

th.commission,
td.commission {
  border-right: 1px solid #cbcbcb;
}
</style>
